import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, toast } from '../../../ui';
import { useCreditCards, useUploadCardInvoice } from '../hooks/useCardInvoices';
import type { CreditCardDTO } from '../services/card-invoices.service';

const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// §14.2: pré-preenche com o mês ANTERIOR ao atual (caso comum: fatura recém-paga).
function previousMonth(): { month: number; year: number } {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

export function ImportarFaturaDialog({ open, onClose, companyId }: Props) {
  const navigate = useNavigate();
  const { data: cardsData } = useCreditCards(companyId, { active: true });
  const cards: CreditCardDTO[] = cardsData?.data ?? [];
  const initial = previousMonth();
  const [cardId, setCardId] = useState('');
  const [referenceMonth, setReferenceMonth] = useState(initial.month);
  const [referenceYear, setReferenceYear] = useState(initial.year);
  const [file, setFile] = useState<File | null>(null);
  const upload = useUploadCardInvoice();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cardId || !file) {
      toast.error('Selecione o cartão e o arquivo da fatura (PDF ou OFX).');
      return;
    }
    try {
      const result = await upload.mutateAsync({ cardId, referenceMonth, referenceYear, file });
      const invoiceId = result?.data?.invoice?.id;
      onClose();
      if (invoiceId) {
        navigate(`/financeiro/faturas/${invoiceId}`);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.existingInvoiceId) {
        toast.error(data?.message || 'Esta fatura já foi importada.');
        navigate(`/financeiro/faturas/${data.existingInvoiceId}`);
        onClose();
        return;
      }
      toast.error(data?.message || err?.message || 'Falha ao importar a fatura.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Importar fatura de cartão">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm text-ink-secondary">Cartão</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
          >
            <option value="">Selecione…</option>
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.issuer} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-sm text-ink-secondary">Fatura com vencimento em</label>
            <select
              className="w-full border rounded px-2 py-1"
              value={referenceMonth}
              onChange={(e) => setReferenceMonth(Number(e.target.value))}
            >
              {MONTH_LABELS.map((label, idx) => (
                <option key={idx + 1} value={idx + 1}>
                  {label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-tertiary">
              O mês em que esta fatura vence — não o mês das compras. As compras podem ser dos meses
              anteriores.
            </p>
          </div>
          <div>
            <label className="text-sm text-ink-secondary">Ano</label>
            <input
              type="number"
              min={2000}
              max={2100}
              className="w-full border rounded px-2 py-1"
              value={referenceYear}
              onChange={(e) => setReferenceYear(Number(e.target.value))}
            />
          </div>
        </div>
        <div>
          <label className="text-sm text-ink-secondary">
            Arquivo da fatura — PDF ou OFX (≤10MB)
          </label>
          <input
            type="file"
            accept=".pdf,.ofx,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full"
          />
          <p className="text-xs text-ink-tertiary mt-1">
            OFX é lido direto (sem IA) — prefira-o quando o emissor oferecer. PDF precisa de camada
            de texto (escaneado não é suportado). Gastos entram na DRE do mês da compra; o pagamento
            aparece no caixa do mês do débito.
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending ? 'Extraindo…' : 'Importar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
